import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@radix-ui/react-icons';
import { Button, PageHeader, Input, Select, Spinner } from '../../components';
import type { SelectOption } from '../../components';
import { useKanbanStore } from '../../store/kanbanStore';
import { useAuthStore } from '../../store/authStore';
import { KANBAN_COLUMNS, kanbanService, mapTaskCardToKanban } from '../../services/kanbanService';
import { getDefaultTaskAssigneeFilter } from '../../utils/taskAssigneeFilter';
import {
  canManageKanbanPipeline,
  canViewAllKanbanAssignees,
  isKanbanAssignedOnly,
} from '../../utils/rbac';
import { KanbanTask, TaskStatus } from '../../types/kanban';
import {
  KanbanSortableTask,
  KanbanTaskCard,
} from './KanbanTaskCard';
import { TaskDetailsModal, TaskDetailsModalMode } from './TaskDetailsModal';
import './KanbanBoard.scss';

type TaskModalState =
  | { open: false }
  | {
      open: true;
      mode: TaskDetailsModalMode;
      task?: KanbanTask;
      columnId?: TaskStatus;
    };

const getDefaultAssigneeFilter = getDefaultTaskAssigneeFilter;

interface KanbanColumnBoardProps {
  columnId: TaskStatus;
  title: string;
  dotColor: string;
  tasks: KanbanTask[];
  totalCount: number;
  canAddItems: boolean;
  onAddItem: (columnId: TaskStatus) => void;
  onOpenTask: (task: KanbanTask) => void;
}

const KanbanColumnBoard: React.FC<KanbanColumnBoardProps> = ({
  columnId,
  title,
  dotColor,
  tasks,
  totalCount,
  canAddItems,
  onAddItem,
  onOpenTask,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: columnId });
  const taskIds = tasks?.map((t) => t.taskId);

  return (
    <div className="kanban-column">
      <header className="kanban-column__header">
        <div className="kanban-column__title-wrap">
          <span
            className="kanban-column__dot"
            style={{ backgroundColor: dotColor }}
            aria-hidden="true"
          />
          <h2 className="kanban-column__title">{title.toUpperCase()}</h2>
          <span className="kanban-column__count">{totalCount}</span>
        </div>
      </header>

      <div
        ref={setNodeRef}
        className={`kanban-column__body ${isOver ? 'kanban-column__body--over' : ''}`}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks?.map((task) => (
            <KanbanSortableTask key={task.taskId} task={task} onOpen={onOpenTask} />
          ))}
        </SortableContext>

        {canAddItems && (
        <button
          type="button"
          className="kanban-column__add"
          onClick={() => onAddItem(columnId)}
        >
          <PlusIcon />
          Add Item
        </button>
        )}
      </div>
    </div>
  );
};

export const KanbanBoard: React.FC = () => {
  const role = useAuthStore((state) => state.role);
  const staffUserId = useAuthStore((state) => state.staffUserId ?? state.user?.id ?? null);
  const assignedOnly = isKanbanAssignedOnly(role);
  const canViewAllAssignees = canViewAllKanbanAssignees(role);
  const canCreateTasks = canManageKanbanPipeline(role);

  const {
    tasks,
    columnCounts,
    assignees,
    taskLabels,
    isLoading,
    error,
    fetchBoard,
    fetchLookups,
    moveTask,
    createTask,
    updateTask,
  } = useKanbanStore();

  const [taskModal, setTaskModal] = useState<TaskModalState>({ open: false });
  const [isSavingTask, setIsSavingTask] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState(getDefaultAssigneeFilter);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState('all');
  const [activeTask, setActiveTask] = useState<KanbanTask | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (assignedOnly && staffUserId) {
      setAssigneeFilter(String(staffUserId));
    }
  }, [assignedOnly, staffUserId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const uiFilters = useMemo(
    () => ({
      search: searchQuery,
      status: statusFilter,
      assignee: assigneeFilter,
      priority: priorityFilter,
      label: labelFilter,
    }),
    [searchQuery, statusFilter, assigneeFilter, priorityFilter, labelFilter]
  );

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchBoard(uiFilters);
    }, delay);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, statusFilter, assigneeFilter, priorityFilter, labelFilter, fetchBoard, uiFilters]);

  const location = useLocation();
  const navigate = useNavigate();
  const openedTaskRef = useRef<string | null>(null);

  useEffect(() => {
    const openTaskId = (location.state as { openTaskId?: string } | null)?.openTaskId;
    if (!openTaskId || isLoading) return;
    if (openedTaskRef.current === openTaskId) return;

    openedTaskRef.current = openTaskId;

    const fromBoard = tasks.find((t) => t.taskId === openTaskId);
    if (fromBoard) {
      setTaskModal({ open: true, mode: 'edit', task: fromBoard });
    } else {
      kanbanService.getTaskById(openTaskId).then((full) => {
        if (full) {
          setTaskModal({
            open: true,
            mode: 'edit',
            task: mapTaskCardToKanban(full),
          });
        }
      });
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [isLoading, location.pathname, location.state, navigate, tasks]);

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'Status' },
      ...KANBAN_COLUMNS.map((c) => ({ value: c.id, label: c.title })),
    ],
    []
  );

  const assigneeOptions: SelectOption[] = useMemo(() => {
    if (assignedOnly && staffUserId) {
      const self = assignees.find((a) => a.userId === Number(staffUserId));
      return [{ value: String(staffUserId), label: self?.name ?? 'My tasks' }];
    }
    if (!canViewAllAssignees) {
      const self = staffUserId
        ? assignees.find((a) => a.userId === Number(staffUserId))
        : undefined;
      return [
        ...(self ? [{ value: String(staffUserId), label: self.name }] : []),
        { value: 'unassigned', label: 'Unassigned' },
      ];
    }
    return [
      { value: 'all', label: 'Assignee' },
      { value: 'unassigned', label: 'Unassigned' },
      ...assignees.map((a) => ({
        value: String(a.userId),
        label: a.name,
      })),
    ];
  }, [assignees, assignedOnly, canViewAllAssignees, staffUserId]);

  const priorityOptions: SelectOption[] = [
    { value: 'all', label: 'Priority' },
    { value: 'high', label: 'High' },
    { value: 'normal', label: 'Normal' },
    { value: 'low', label: 'Low' },
  ];

  const labelOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'Label' },
      ...taskLabels.map((l) => ({
        value: String(l.labelId),
        label: l.name,
      })),
    ],
    [taskLabels]
  );

  const tasksByColumn = useMemo(() => {
    const grouped: Record<TaskStatus, KanbanTask[]> = {
      un_assigned: [],
      todo: [],
      waiting_for_info: [],
      in_progress: [],
      completed: [],
    };

    tasks.forEach((task) => {
      if (grouped[task.columnId]) {
        grouped[task.columnId].push(task);
      }
    });

    Object.keys(grouped).forEach((key) => {
      grouped[key as TaskStatus].sort((a, b) => a.order - b.order);
    });

    return grouped;
  }, [tasks]);

  const visibleColumns = useMemo(() => {
    if (statusFilter === 'all') return KANBAN_COLUMNS;
    return KANBAN_COLUMNS.filter((c) => c.id === statusFilter);
  }, [statusFilter]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const task = tasks.find((t) => t.taskId === event.active.id);
      setActiveTask(task ?? null);
    },
    [tasks]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveTask(null);
      const { active, over } = event;
      if (!over) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      const activeTaskItem = tasks.find((t) => t.taskId === activeId);
      if (!activeTaskItem) return;

      const isOverColumn = KANBAN_COLUMNS.some((c) => c.id === overId);
      const overTask = tasks.find((t) => t.taskId === overId);

      const targetColumnId = (
        isOverColumn ? overId : overTask?.columnId ?? activeTaskItem.columnId
      ) as TaskStatus;

      if (activeTaskItem.columnId === targetColumnId) return;

      await moveTask(activeId, targetColumnId);
    },
    [tasks, moveTask]
  );

  const handleDragCancel = useCallback(() => {
    setActiveTask(null);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
    setAssigneeFilter(
      assignedOnly && staffUserId
        ? String(staffUserId)
        : canViewAllAssignees
          ? 'all'
          : staffUserId
            ? String(staffUserId)
            : 'all'
    );
    setPriorityFilter('all');
    setLabelFilter('all');
  }, [assignedOnly, canViewAllAssignees, staffUserId]);

  const handleAddTask = useCallback(() => {
    setTaskModal({ open: true, mode: 'create', columnId: 'un_assigned' });
  }, []);

  const handleAddItem = useCallback((columnId: TaskStatus) => {
    setTaskModal({ open: true, mode: 'create', columnId });
  }, []);

  const handleOpenTask = useCallback((task: KanbanTask) => {
    setTaskModal({ open: true, mode: 'edit', task });
  }, []);

  const handleCloseTaskModal = useCallback(() => {
    setTaskModal({ open: false });
  }, []);

  const handleSaveTask = useCallback(
    async (payload: Parameters<typeof createTask>[0], taskId?: string) => {
      setIsSavingTask(true);
      try {
        if (taskId) {
          await updateTask(taskId, payload);
        } else {
          await createTask(payload);
        }
        setTaskModal({ open: false });
      } finally {
        setIsSavingTask(false);
      }
    },
    [createTask, updateTask]
  );

  return (
    <div className="kanban-board">
      <PageHeader
        title="CRM Kanban Board"
        subtitle="Manage and track your lead pipeline efficiently across stages."
        actions={
          canCreateTasks ? (
          <Button className="kanban-board__add-btn" onClick={handleAddTask}>
            <PlusIcon />
            Add New Tasks
          </Button>
          ) : undefined
        }
      />

      {error && <div className="kanban-board__error">{error.message}</div>}

      <div className="kanban-board__filters">
        <div className="kanban-board__search">
          <MagnifyingGlassIcon className="kanban-board__search-icon" aria-hidden="true" />
          <Input
            placeholder="Find leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="kanban-board__search-input"
          />
        </div>

        <div className="kanban-board__filter-select">
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            searchable={false}
            className="kanban-board__select"
          />
          <ChevronDownIcon className="kanban-board__select-chevron" aria-hidden="true" />
        </div>

        <div className="kanban-board__filter-select">
          <Select
            value={assigneeFilter}
            onChange={setAssigneeFilter}
            options={assigneeOptions}
            searchable={false}
            disabled={assignedOnly}
            className="kanban-board__select"
          />
          <ChevronDownIcon className="kanban-board__select-chevron" aria-hidden="true" />
        </div>

        <div className="kanban-board__filter-select">
          <Select
            value={priorityFilter}
            onChange={setPriorityFilter}
            options={priorityOptions}
            searchable={false}
            className="kanban-board__select"
          />
          <ChevronDownIcon className="kanban-board__select-chevron" aria-hidden="true" />
        </div>

        <div className="kanban-board__filter-select">
          <Select
            value={labelFilter}
            onChange={setLabelFilter}
            options={labelOptions}
            searchable={false}
            className="kanban-board__select"
          />
          <ChevronDownIcon className="kanban-board__select-chevron" aria-hidden="true" />
        </div>

        <button
          type="button"
          className="kanban-board__clear"
          onClick={handleClearFilters}
        >
          Clear all
        </button>
      </div>

      {isLoading && tasks.length === 0 ? (
        <div className="kanban-board__loading">
          <Spinner size="medium" />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="kanban-board__columns">
            {visibleColumns.map((column) => (
              <KanbanColumnBoard
                key={column.id}
                columnId={column.id}
                title={column.title}
                dotColor={column.dotColor}
                tasks={tasksByColumn[column.id]}
                totalCount={columnCounts[column.id]}
                canAddItems={canCreateTasks}
                onAddItem={handleAddItem}
                onOpenTask={handleOpenTask}
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              <KanbanTaskCard task={activeTask} isDragging />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskDetailsModal
        isOpen={taskModal.open}
        mode={taskModal.open ? taskModal.mode : 'create'}
        task={taskModal.open ? taskModal.task : null}
        defaultColumnId={
          taskModal.open ? taskModal.columnId ?? 'un_assigned' : 'un_assigned'
        }
        onClose={handleCloseTaskModal}
        onSave={handleSaveTask}
        isSaving={isSavingTask}
      />
    </div>
  );
};
